'use client';

/**
 * EntityMetadataFields Component
 * Type-specific metadata fields for entities
 */

import type { EntityType, EntityMetadata } from '@/lib/command-center/types';

interface MetadataFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'url' | 'number' | 'date' | 'select' | 'currency';
  placeholder?: string;
  options?: { value: string; label: string }[];
  prefix?: string;
}

// Type-specific field configurations
const TYPE_FIELD_CONFIGS: Partial<Record<EntityType, MetadataFieldConfig[]>> = {
  technology: [
    { key: 'version', label: 'Version', type: 'text', placeholder: 'e.g., 2.1.0' },
    { key: 'repository', label: 'Repository URL', type: 'url', placeholder: 'https://github.com/...' },
    { key: 'documentation', label: 'Documentation URL', type: 'url', placeholder: 'https://docs...' },
    { key: 'license', label: 'License', type: 'select', options: [
      { value: 'open-source', label: 'Open Source' },
      { value: 'commercial', label: 'Commercial' },
      { value: 'freemium', label: 'Freemium' },
      { value: 'enterprise-only', label: 'Enterprise Only' },
    ]},
    { key: 'category', label: 'Category', type: 'text', placeholder: 'e.g., Database, Framework' },
  ],
  infrastructure: [
    { key: 'provider', label: 'Provider', type: 'text', placeholder: 'e.g., AWS, GCP, Azure' },
    { key: 'region', label: 'Region', type: 'text', placeholder: 'e.g., us-east-1' },
    { key: 'pricing.monthlyCost', label: 'Monthly Cost', type: 'currency', prefix: '$' },
    { key: 'documentation', label: 'Documentation URL', type: 'url' },
  ],
  application: [
    { key: 'repository', label: 'Repository URL', type: 'url', placeholder: 'https://github.com/...' },
    { key: 'documentation', label: 'Documentation URL', type: 'url' },
    { key: 'website', label: 'Website', type: 'url' },
    { key: 'version', label: 'Version', type: 'text', placeholder: 'e.g., 3.0.0' },
  ],
  team: [
    { key: 'lead', label: 'Team Lead', type: 'text', placeholder: 'Name of team lead' },
    { key: 'size', label: 'Team Size', type: 'number', placeholder: 'Number of members' },
    { key: 'email', label: 'Team Email', type: 'text', placeholder: 'team@company.com' },
    { key: 'slackChannel', label: 'Slack Channel', type: 'text', placeholder: '#team-channel' },
    { key: 'oncall', label: 'On-Call Rotation', type: 'text', placeholder: 'PagerDuty link or schedule' },
    { key: 'costCenter', label: 'Cost Center', type: 'text', placeholder: 'e.g., ENG-001' },
  ],
  vendor: [
    { key: 'website', label: 'Website', type: 'url' },
    { key: 'primaryContact', label: 'Primary Contact', type: 'text', placeholder: 'Name or email' },
    { key: 'contractExpiry', label: 'Contract Expiry', type: 'date' },
    { key: 'annualSpend', label: 'Annual Spend', type: 'currency', prefix: '$' },
    { key: 'tier', label: 'Vendor Tier', type: 'select', options: [
      { value: 'critical', label: 'Critical' },
      { value: 'important', label: 'Important' },
      { value: 'standard', label: 'Standard' },
    ]},
    { key: 'soc2Report', label: 'SOC 2 Report URL', type: 'url' },
    { key: 'lastReview', label: 'Last Review Date', type: 'date' },
  ],
  compliance_framework: [
    { key: 'fullName', label: 'Full Name', type: 'text', placeholder: 'e.g., General Data Protection Regulation' },
    { key: 'documentation', label: 'Documentation URL', type: 'url' },
    { key: 'renewalPeriod', label: 'Renewal Period', type: 'text', placeholder: 'e.g., Annual' },
    { key: 'lastAudit', label: 'Last Audit Date', type: 'date' },
    { key: 'nextAudit', label: 'Next Audit Date', type: 'date' },
    { key: 'auditor', label: 'Auditor', type: 'text', placeholder: 'Name of auditing firm' },
  ],
  api: [
    { key: 'documentation', label: 'API Docs URL', type: 'url' },
    { key: 'version', label: 'API Version', type: 'text', placeholder: 'e.g., v2' },
    { key: 'website', label: 'Base URL', type: 'url', placeholder: 'https://api.example.com' },
  ],
  data_store: [
    { key: 'category', label: 'Database Type', type: 'text', placeholder: 'e.g., PostgreSQL, Redis' },
    { key: 'version', label: 'Version', type: 'text' },
    { key: 'documentation', label: 'Documentation URL', type: 'url' },
  ],
  process: [
    { key: 'documentation', label: 'Process Doc URL', type: 'url' },
    { key: 'owner', label: 'Process Owner', type: 'text' },
  ],
  stakeholder: [
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'role', label: 'Role/Title', type: 'text' },
  ],
};

// Default fields for types without specific config
const DEFAULT_FIELDS: MetadataFieldConfig[] = [
  { key: 'website', label: 'Website', type: 'url' },
  { key: 'documentation', label: 'Documentation', type: 'url' },
];

interface EntityMetadataFieldsProps {
  entityType: EntityType;
  metadata: EntityMetadata;
  onChange: (metadata: EntityMetadata) => void;
  isEditing: boolean;
}

export default function EntityMetadataFields({
  entityType,
  metadata,
  onChange,
  isEditing,
}: EntityMetadataFieldsProps) {
  const fields = TYPE_FIELD_CONFIGS[entityType] || DEFAULT_FIELDS;

  const getValue = (key: string): string => {
    // Handle nested keys like 'pricing.monthlyCost'
    const parts = key.split('.');
    let value: unknown = metadata;
    for (const part of parts) {
      value = (value as Record<string, unknown>)?.[part];
    }
    return value as string || '';
  };

  const setValue = (key: string, value: string) => {
    const parts = key.split('.');
    const newMetadata = { ...metadata };

    if (parts.length === 1) {
      (newMetadata as Record<string, unknown>)[key] = value || undefined;
    } else {
      // Handle nested keys
      const [parent, child] = parts;
      (newMetadata as Record<string, unknown>)[parent] = {
        ...(metadata[parent as keyof EntityMetadata] as Record<string, unknown> || {}),
        [child]: value || undefined,
      };
    }

    onChange(newMetadata);
  };

  const formatDisplayValue = (field: MetadataFieldConfig, value: string): string | React.ReactNode => {
    if (!value) return <span className="text-foreground-muted italic">Not set</span>;

    if (field.type === 'url') {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:text-accent break-all"
        >
          {value}
        </a>
      );
    }

    if (field.type === 'currency') {
      return `${field.prefix || ''}${Number(value).toLocaleString()}`;
    }

    if (field.type === 'date') {
      try {
        return new Date(value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } catch {
        return value;
      }
    }

    if (field.type === 'select' && field.options) {
      const option = field.options.find(o => o.value === value);
      return option?.label || value;
    }

    return value;
  };

  // Check if any metadata values exist
  const hasAnyMetadata = fields.some(field => getValue(field.key));

  if (!isEditing && !hasAnyMetadata) {
    return null;
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm text-foreground-tertiary mb-2">
        {entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Details
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map((field) => {
          const value = getValue(field.key);

          // In view mode, only show fields that have values
          if (!isEditing && !value) return null;

          return (
            <div key={field.key} className={field.type === 'url' ? 'md:col-span-2' : ''}>
              <label className="block text-xs text-foreground-tertiary mb-1">{field.label}</label>

              {isEditing ? (
                field.type === 'select' && field.options ? (
                  <select
                    value={value}
                    onChange={(e) => setValue(field.key, e.target.value)}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:border-accent focus:outline-none"
                  >
                    <option value="">Select...</option>
                    {field.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="relative">
                    {field.prefix && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-tertiary">
                        {field.prefix}
                      </span>
                    )}
                    <input
                      type={field.type === 'number' || field.type === 'currency' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      value={value}
                      onChange={(e) => setValue(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className={`w-full bg-background border border-border rounded-lg py-2 text-foreground text-sm focus:border-accent focus:outline-none ${
                        field.prefix ? 'pl-7 pr-3' : 'px-3'
                      }`}
                    />
                  </div>
                )
              ) : (
                <p className="text-foreground text-sm">
                  {formatDisplayValue(field, value)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
